/**
 * CAMPAIGN W1.6 — ACT 0: THE COLD OPEN. Beat data only (pure constants).
 * Prose adapted from RLM/specs/narrative/05-story-arc.md and
 * 06-screenplay-scenes.md Scene 1. Subtext over text. Trust the silence.
 */
import type { CampaignBeat } from './campaign.js';
import { surfaceMarkerOffset } from '../signal.js';

/** The Cenotaph scripted signal — Earth-anchored (the arc places the monument
 * in Earth orbit; spawn is Earth-local, so the cold open and every "return to
 * the Cenotaph" beat are spawn-local while Mars stays a real journey).
 * Reuses the legacy id so CenotaphRenderer + verify lore keep working. */
const CENOTAPH_OFFSET_KM = { x: 30_000, y: 8_000, z: -12_000 };

export const CENOTAPH_SIGNAL = {
  id: 'story-cenotaph-vigil',
  anchor: { body: 'Earth' },
  offsetKm: CENOTAPH_OFFSET_KM,
  markerOffsetKm: surfaceMarkerOffset(CENOTAPH_OFFSET_KM, 70),
  kind: 'memorial' as const,
  title: 'THE CENOTAPH — PANEL 4-771',
  threat: 0,
  seed: 4722049, // 47s + 2049 — the Silence
};

export const ACT0_BEATS: CampaignBeat[] = [
  {
    id: 'a0-cold-open',
    act: 0,
    comms: {
      id: 'c-cold-open',
      lines: [
        { speaker: 'PANEL 4-771', text: 'Interior hemisphere. Earth-light through the open mouth of the monument. Names, each as tall as a person. Tens of millions of names.' },
        { speaker: 'VEY', text: 'Halis. With the H tied to the A.' },
        { speaker: 'PANEL 4-771', text: 'The stylus finishes the K. Moves to the next letter.' },
        { speaker: 'JEM', text: "You're early." },
        { speaker: 'VEY', text: "I'm finishing 4-771." },
        { speaker: 'JEM', text: 'Finish it later.' },
        { speaker: 'VEY', text: 'What is it.' },
        { speaker: 'JEM', text: 'Come down off the wall.' },
        { speaker: 'JEM', text: 'I have a name I need verified.' },
        { speaker: 'VEY', text: "That's not — that's not the work. We carve what the Roll gives us." },
        { speaker: 'JEM', text: 'I know what the work is, child.' },
        { speaker: 'SLATE', text: 'HALIS TENDREY. The name glows faint amber.' },
        { speaker: 'VEY', text: 'What about her.' },
        { speaker: 'JEM', text: 'He.' },
        { speaker: 'VEY', text: 'What about him.' },
        { speaker: 'JEM', text: 'The Concord census has him alive. Active courier. Last login this morning at the Phobos drydock.' },
        { speaker: 'PANEL 4-771', text: 'Behind them: panel 4-771, near the bottom. A name freshly carved, three weeks old. HALIS TENDREY. Same spelling. Same tied H.' },
        { speaker: 'VEY', text: "That's a clerical —" },
        { speaker: 'JEM', text: "It's the fourth one this year." },
        { speaker: 'VEY', text: 'Who carved it.' },
        { speaker: 'JEM', text: 'I did.' },
        { speaker: 'JEM', text: 'I want you to find him. And I want you to ask him a question. Whether he wants to come down off the wall.' },
        { speaker: 'PANEL 4-771', text: 'Earth turns silently behind them. The letter on the wall stands unfinished. The stylus is still in your hand.' },
      ],
      // CAMPAIGN §8 — the silent Cenotaph Choice. No [1]/[2] menu: the last line
      // hands off to interactive control at the bench. The player's hand decides —
      // hold [V] finishes the K (cenotaphChoice='finished'); undock leaves it
      // half-cut ('left'). Nothing is labelled; the game records it and never says
      // so. The client emits CENOTAPH_CHOICE_MADE; the flag key/values are unchanged
      // from the old menu, so the Waypoint-7 somatic callback stays valid.
    },
    advance: [{ on: 'CENOTAPH_CHOICE_MADE', next: 'a0-first-flight' }],
  },
  {
    id: 'a0-first-flight',
    act: 0,
    storySignals: [CENOTAPH_SIGNAL],
    objective: {
      id: 'camp-obj-slate',
      kind: 'investigate',
      status: 'ACTIVE',
      target: 'camp-phobos-drydock',
      progress: 0,
      goal: 1,
      reward: 0,
      briefing: 'TAKE THE SLATE — Halis Tendrey, last login the Phobos drydock. Engage supercruise [X] for Mars.',
    },
    advance: [{ on: 'CRUISE_ENGAGED', next: 'a1-title' }],
  },
];
